import { useMemo } from "react";
import { Eye, MousePointerClick, Percent, Globe } from "lucide-react";
import { HeadlineTiles, WhoSawIt, BestAds, WhereAndWhat, rankAds, fmt } from "./StoryParts";
import { audienceGroups, audienceStory, deviceRows, deviceGapNote } from "./storyData";
import TrendsPanel from "./TrendsPanel";

const BENCHMARK = 0.07;

export default function SocialMirrorStoryLayout({ clientName, socialMirrorSummary, CreativeIcon, clientId, API, readOnly, reportStart, reportEnd }) {
  const d = useMemo(() => {
    const s = socialMirrorSummary || {};
    const campaigns = s.campaigns || [];
    const items = campaigns.flatMap((c) => c.line_items || []).map((i) => ({ name: i.name, impressions: i.impressions, clicks: i.clicks }));
    const devices = Object.entries(s.device_breakdown || {}).map(([device, v]) => ({ device, impressions: v.impressions || 0 }));
    return {
      o: s.overview || {},
      aud: audienceGroups(items),
      ads: rankAds((s.top_creatives || []).map((c) => ({ name: c.name || c.ad_name, impressions: c.impressions, clicks: c.clicks }))),
      dev: deviceRows(devices),
    };
  }, [socialMirrorSummary]);

  const { o } = d;
  if (!(o.total_impressions > 0)) return null;
  const ctr = parseFloat(o.ctr) || 0;
  const visits = (o.click_conversions || 0) + (o.view_through_conversions || 0);
  const tiles = [
    { icon: Eye, label: "Times seen", value: fmt(o.total_impressions), testId: "sm-kpi-impressions" },
    { icon: MousePointerClick, label: "Clicks", value: fmt(o.total_clicks), sub: "People who tapped to learn more", testId: "sm-kpi-clicks" },
    { icon: Percent, label: "Click rate", value: `${ctr.toFixed(2)}%`, sub: "Clicks per ad seen", testId: "sm-kpi-ctr" },
  ];
  if (visits > 0) {
    tiles.push({ icon: Globe, label: "Website visits", value: fmt(visits), sub: `${fmt(o.click_conversions)} after a click · ${fmt(o.view_through_conversions)} after a view`, testId: "sm-kpi-visits" });
  }
  return (
    <div data-testid="sm-story-layout">
      <HeadlineTiles tiles={tiles} gauge={{ ctr, benchmark: BENCHMARK, label: "vs typical display ad", testId: "sm-benchmark-gauge" }} />
      <TrendsPanel clientId={clientId} clientName={clientName} API={API} readOnly={readOnly} reportStart={reportStart} reportEnd={reportEnd}
        endpoint="social-mirror-trends" channel="social_mirror" benchmark={BENCHMARK} benchmarkLabel="Typical display ad click rate" channelLabel="Social Mirror" p="sm" showHighlights={false} />
      {d.aud && <WhoSawIt groups={d.aud.groups} back={d.aud.back} fresh={d.aud.fresh} story={audienceStory(d.aud.back, d.aud.fresh)} testPrefix="sm" />}
      {d.ads.length > 0 && <BestAds ads={d.ads} CreativeIcon={CreativeIcon} campaignType="social_mirror" benchmark={BENCHMARK} testPrefix="sm" />}
      <WhereAndWhat testId="sm-where-what" donut sections={[
        { title: "What people saw them on", rows: d.dev.rows, note: deviceGapNote(o.total_impressions, d.dev.total), testId: "sm-devices" },
      ]} />
    </div>
  );
}
